import {
  FocusEventHandler,
  JSX,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import _ from 'lodash';
import {
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CopyIcon,
  InfoIcon,
} from 'lucide-react';

import { Button } from 'components/Button';
import KitDefaultProfitTooltip from 'components/Kit/KitDefaultProfitTooltip';
import { Label } from 'components/Label';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/Tooltip';
import { Input } from 'components/input';
import { Divider, Flex } from 'components/layout';
import { QuickQuoteCustomAccessorialTooltipRow } from 'components/pricing/QuickQuoteCustomAccessorialTooltipRow';
import { tqlBillRateEditCursor } from 'components/pricing/tqlBillRateEditCursor';
import { useFuelSurchargeAutoSelect } from 'components/pricing/useFuelSurchargeAutoSelect';
import { Typography } from 'components/typography/Typography';
import { SidebarStateContext } from 'contexts/sidebarStateContext';
import { useServiceFeatures } from 'hooks/useServiceContext';
import { toast } from 'hooks/useToaster';
import { getFuelPriceEIA } from 'lib/api/getFuelPriceEIA';
import { QuickQuoteCustomAccessorial } from 'lib/api/getQuickQuoteCustomAccessorials';
import {
  DistanceSource,
  FuelType,
} from 'pages/QuoteView/Quoting/RequestQuickQuote/QuickQuoteForm';
import {
  QuoteTerminatingAction,
  QuoteValueType,
} from 'pages/QuoteView/Quoting/RequestQuickQuote/constants';
import { QuickQuoteContext } from 'pages/QuoteView/Quoting/RequestQuickQuote/context/QuickQuoteContext';
import { useQuickQuoteCurrency } from 'pages/QuoteView/Quoting/RequestQuickQuote/hooks/useQuickQuoteCurrency';
import {
  CarrierCostType,
  ProfitType,
  QuoteMode,
  SelectedQuoteType,
} from 'pages/QuoteView/Quoting/RequestQuickQuote/types';
import {
  convertDisplayToUsd,
  convertUsdForDisplay,
} from 'pages/QuoteView/Quoting/RequestQuickQuote/utils/currencyFormatting';
import { Maybe, MaybeUndef } from 'types/UtilityTypes';
import { CurrencyOption } from 'types/enums/CurrencyOptions';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';
import {
  Quoting,
  QuotingPortals,
  integrationNameMap,
} from 'types/enums/Integrations';
import captureException from 'utils/captureException';
import { copyToClipboard } from 'utils/copyToClipboard';
import { sumFlatAccessorialCharges } from 'utils/parseFlatAccessorialCharge';
import { calculateProfitFromFinalPrice } from 'utils/priceCalculations';
import { isTqlInboxEmail } from 'utils/quickQuoteDefaults';
import { cn } from 'utils/shadcn';

export enum CarrierPriceCalculatorParent {
  QuickQuote = 'QuickQuote',
  CarrierQuote = 'CarrierQuote',
}

export type CarrierPriceCalculatorProps = {
  defaultFSCProvider?: string;
  parentQuoteRequestId: number;
  showTitle: boolean;
  mileage: number;
  mileageSource: Maybe<DistanceSource>;
  finalPrice: Maybe<number>;
  fuelEstimate: number;
  portalFuelSurchargeSource: Maybe<QuotingPortals>;
  portalFuelSurcharge: Maybe<number>; // FSC AI-parsed from bidding portals like Freightview, E2Open, etc.
  datFuelSurcharge: Maybe<number>;
  kencoFuelSurcharge?: Maybe<number>;
  // Customer FSC table rate ($/mi). 0 is a valid matched rate, null/undefined = no match.
  customerTableFuelSurcharge?: MaybeUndef<number>;
  profit: number;
  setProfitHandler?:
    React.Dispatch<React.SetStateAction<number>> | ((value: number) => void);
  profitType: ProfitType;
  setProfitTypeHandler:
    | React.Dispatch<React.SetStateAction<ProfitType>>
    | ((value: ProfitType) => void);
  maxDistance: number;
  carrierCost: number;
  carrierCostType: CarrierCostType;
  /**
   * We capture implicit edits the user makes to the quote in the background (see useConfirmationPing
   * but when they take an explicit action (e.g. copy, reply email, input submit),
   * we need to call this handler to capture the action and update the quote request suggestion with the status
   * and the final quote the user uses.
   * @param action - The action the user took (e.g. copy, reply, input, submit)
   * @param quotedValueType - The value type the user quoted (e.g. linehaul, total_sell)
   * @returns
   */
  terminatingActionHandler: (
    action?: QuoteTerminatingAction,
    quotedValueType?: QuoteValueType
  ) => Promise<void>;
  setCarrierCostTypeHandler: React.Dispatch<
    React.SetStateAction<CarrierCostType>
  >;
  onBlurHandler?: FocusEventHandler<HTMLInputElement>;
  setIsCarrierButtonClickedHandler?: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  setSelectedQuoteIndexHandler?: React.Dispatch<
    React.SetStateAction<Maybe<number>>
  >;
  setCarrierCostHandler?: React.Dispatch<React.SetStateAction<number>>;
  setFuelEstimateHandler?: React.Dispatch<React.SetStateAction<number>>;
  setFinalPriceHandler?: React.Dispatch<React.SetStateAction<Maybe<number>>>;
  selectedQuickQuoteId?: number;
  calculatorParent: CarrierPriceCalculatorParent;
  stopFee?: number;
  selectedQuoteType?: SelectedQuoteType;
  availableCustomAccessorials?: QuickQuoteCustomAccessorial[];
  // Custom (Non-TMS) accessorials selected by the user.
  selectedCustomAccessorials?: string[];
};

// Add your dev domain to view alternative labels
// TODO: Add settings page to Drumkit Portal
const altLabelsDomains = ['fetchfreight.com'];

interface BuySellLabels {
  buyLabel: string;
  sellLabel: string;
}

function getBuySellLabels(
  inboxEmailAddress: string,
  isTql: boolean
): BuySellLabels {
  if (isTql) {
    const tqlLabels: BuySellLabels = {
      buyLabel: 'Truck Pay',
      sellLabel: 'Bill Rate',
    };
    return tqlLabels;
  }

  const usesAltLabels = altLabelsDomains.some((domain) =>
    inboxEmailAddress.toLowerCase().includes(domain.toLowerCase())
  );
  if (usesAltLabels) {
    const altLabels: BuySellLabels = { buyLabel: 'TTT', sellLabel: 'TTC' };
    return altLabels;
  }

  const defaultLabels: BuySellLabels = { buyLabel: 'Buy', sellLabel: 'Sell' };
  return defaultLabels;
}

// TIP: Do NOT use _.round() on any of the intermediate *numeric* values in this component as this causes precision issues
// when switching from flat to per mile or vice versa.
// Instead, always perform numeric calculations using floating point arithmetic, then call formatCostByType() and formatMarginByType()
// to format the results as strings with the correct number of decimal places.
export default function CarrierPriceCalculator({
  parentQuoteRequestId,
  showTitle,
  mileage: initMileage,
  defaultFSCProvider,
  finalPrice,
  fuelEstimate,
  datFuelSurcharge,
  kencoFuelSurcharge,
  customerTableFuelSurcharge,
  portalFuelSurchargeSource,
  portalFuelSurcharge,
  profit,
  setProfitHandler,
  profitType,
  setProfitTypeHandler,
  carrierCost,
  carrierCostType,
  terminatingActionHandler,
  setCarrierCostTypeHandler,
  onBlurHandler,
  setIsCarrierButtonClickedHandler,
  setSelectedQuoteIndexHandler,
  setCarrierCostHandler,
  setFuelEstimateHandler,
  setFinalPriceHandler,
  calculatorParent,
  stopFee,
  selectedQuoteType,
  availableCustomAccessorials,
  selectedCustomAccessorials,
}: CarrierPriceCalculatorProps): JSX.Element {
  const {
    currentState: { inboxEmailAddress },
  } = useContext(SidebarStateContext);
  const isTql = isTqlInboxEmail(inboxEmailAddress);
  const quickQuoteContext = useContext(QuickQuoteContext);
  const setBillRateEditSessionActive =
    quickQuoteContext?.setIsBillRateEditSessionActive;
  const { buyLabel, sellLabel } = getBuySellLabels(inboxEmailAddress, isTql);

  const syncBillRateEditSessionActive = useCallback(
    (active: boolean) => {
      if (calculatorParent !== CarrierPriceCalculatorParent.QuickQuote) {
        return;
      }
      setBillRateEditSessionActive?.(active);
    },
    [calculatorParent, setBillRateEditSessionActive]
  );

  useEffect(() => {
    return () => {
      setBillRateEditSessionActive?.(false);
    };
  }, [setBillRateEditSessionActive]);
  const sellRatePhrase = isTql ? 'Bill Rate' : 'Sell Rate';

  const selectedCustomAccessorialDetails = useMemo(() => {
    const names = Array.isArray(selectedCustomAccessorials)
      ? selectedCustomAccessorials.filter(
          (n): n is string => typeof n === 'string' && n.trim() !== ''
        )
      : [];
    const catalog = availableCustomAccessorials ?? [];
    const byName = new Map(catalog.map((a) => [a.name, a]));

    return names.map((name) => {
      const found = byName.get(name);
      if (found) {
        return found;
      }

      return {
        quoteType: QuoteMode.TL,
        name,
        description: '',
        charge: '',
        notes: '',
      } satisfies QuickQuoteCustomAccessorial;
    });
  }, [selectedCustomAccessorials, availableCustomAccessorials]);

  const {
    serviceFeaturesEnabled: {
      isQuoteCalculatorMarginEnabled,
      isQuickQuoteFuelDetailsEnabled,
    },
    serviceID,
  } = useServiceFeatures();
  const {
    isEnabled: isCurrencyToggleEnabled,
    formatAmount,
    formatPerMileRate,
    inputPrefix,
    toDisplayAmount,
    fromDisplayAmount,
    selectedCurrency,
  } = useQuickQuoteCurrency();
  const isQuoteCurrencyEnabled =
    calculatorParent === CarrierPriceCalculatorParent.QuickQuote &&
    isCurrencyToggleEnabled;
  const displayInputPrefix = isQuoteCurrencyEnabled ? inputPrefix : '$';
  const isCadDisplay =
    isQuoteCurrencyEnabled && selectedCurrency === CurrencyOption.CAD;
  const prevQuoteCurrencyRef = useRef(selectedCurrency);
  const lastAppliedParseableAccessorialSumRef = useRef(0);
  const currencyPrefixClass = cn(
    'pointer-events-none absolute text-sm text-neutral-400',
    isCadDisplay ? 'left-2' : 'left-1 xxs:left-2'
  );
  const sellCurrencyPrefixClass = cn(
    'pointer-events-none absolute text-lg font-bold text-brand-400',
    isCadDisplay ? 'left-2' : 'left-1'
  );
  const currencyInputPaddingClass = isCadDisplay ? 'pl-10' : 'pl-4';
  const sellInputPaddingClass = isCadDisplay ? 'pl-12!' : 'pl-4.5';

  const formatCostDisplay = useCallback(
    (amount: number, type: CarrierCostType) => {
      return formatCostByType(toDisplayAmount(amount), type);
    },
    [toDisplayAmount]
  );

  const formatMoneyLabel = useCallback(
    (amount: number, type: CarrierCostType) => {
      if (type === CarrierCostType.PerMile) {
        if (!isQuoteCurrencyEnabled) {
          return `$${formatCostByType(amount, type)}`;
        }

        return formatPerMileRate(amount, 2);
      }

      if (!isQuoteCurrencyEnabled) {
        return `$${formatCostByType(amount, type)}`;
      }

      return formatAmount(amount, 0);
    },
    [formatAmount, formatPerMileRate, isQuoteCurrencyEnabled]
  );

  // Format input values as strings to avoid floating point errors
  const [mileage, setMileage] = useState(initMileage);
  const [accessorialsDollarsInput, setAccessorialsDollarsInput] = useState('');

  const accessorialsDollarsNumeric = useMemo(() => {
    const n = parseFloat(accessorialsDollarsInput);
    if (!Number.isFinite(n) || n < 0) {
      return 0;
    }
    return fromDisplayAmount(n);
  }, [accessorialsDollarsInput, fromDisplayAmount]);

  const accessorialsAddInQuoteUnits = useMemo(() => {
    if (accessorialsDollarsNumeric <= 0) {
      return 0;
    }
    if (carrierCostType === CarrierCostType.Flat) {
      return accessorialsDollarsNumeric;
    }

    return mileage > 0 ? accessorialsDollarsNumeric / mileage : 0;
  }, [accessorialsDollarsNumeric, carrierCostType, mileage]);
  const [buyInputValue, setBuyInputValue] = useState(
    formatCostByType(carrierCost, carrierCostType)
  );
  const [sellInputValue, setSellInputValue] = useState(
    formatCostByType(finalPrice ?? 0, carrierCostType)
  );
  const [isSellRateEditMode, setIsSellRateEditMode] = useState(false);
  const isSellTypingActive = !isTql || isSellRateEditMode;
  const isEditingSellRef = useRef(false);
  const isSellRateEditModeRef = useRef(false);
  const sellInputRef = useRef<HTMLInputElement>(null);

  const [isDefaultProfit, setIsDefaultProfit] = useState(true);
  const [isDefaultProfitType, setIsDefaultProfitType] = useState(true);
  const [profitInputValue, setProfitInputValue] = useState(
    formatProfitByType(profit, carrierCostType, profitType)
  );
  const [hasCopiedSellPrice, setHasCopiedSellPrice] = useState(false);
  const [hasCopiedLinehaulPrice, setHasCopiedLinehaulPrice] = useState(false);

  const [isQuoteDetailsOpen, setIsQuoteDetailsOpen] = useState(false);
  const [fuelPriceEIA, setFuelPriceEIA] = useState<Maybe<number>>(null);

  const [fuelSurcharge, setFuelSurcharge] = useState<number>(0.3);
  const [fuelSurchargeInputValue, setFuelSurchargeInputValue] =
    useState('0.30');
  const fuelSurchargeRef = useRef(fuelSurcharge);
  fuelSurchargeRef.current = fuelSurcharge;
  const prevFuelDisplayCurrencyRef = useRef(selectedCurrency);
  const [selectedFuelType, setSelectedFuelType] = useState<FuelType>(
    FuelType.None
  );

  const applyFuelTypeSelection = useCallback(
    (newType: FuelType) => {
      setSelectedFuelType(newType);

      let newFuelSurchargeValue: number;

      switch (newType) {
        case FuelType.DAT:
          newFuelSurchargeValue = datFuelSurcharge ?? 0;
          break;
        case FuelType.DOE:
          if (fuelPriceEIA != null) {
            // 14.3% of Diesel gallon price = Assuming 7 miles-per-gallon (1/7 = 0.143)
            newFuelSurchargeValue = _.round(fuelPriceEIA * 0.143, 2);
          } else {
            newFuelSurchargeValue = 0;
          }
          break;
        case FuelType.Kenco:
          newFuelSurchargeValue = kencoFuelSurcharge ?? 0;
          break;
        case FuelType.CustomerTable:
          newFuelSurchargeValue = customerTableFuelSurcharge ?? 0;
          break;
        case FuelType.Portal:
          newFuelSurchargeValue = portalFuelSurcharge ?? 0;
          break;
        default:
          newFuelSurchargeValue = 0;
      }

      setFuelSurcharge(newFuelSurchargeValue);
      setFuelSurchargeInputValue(
        toDisplayAmount(newFuelSurchargeValue).toFixed(2)
      );
    },
    [
      customerTableFuelSurcharge,
      datFuelSurcharge,
      fuelPriceEIA,
      kencoFuelSurcharge,
      portalFuelSurcharge,
      toDisplayAmount,
    ]
  );

  const clearFuelTypeAttribution = useCallback(() => {
    setSelectedFuelType(FuelType.None);
  }, []);

  const { selectUserFuelType, markManualFuelEntry } =
    useFuelSurchargeAutoSelect({
      parentQuoteRequestId,
      selectedQuoteType,
      defaultFSCProvider,
      datFuelSurcharge,
      portalFuelSurcharge,
      customerTableFuelSurcharge,
      kencoFuelSurcharge,
      fuelPriceEIA,
      applyFuelTypeSelection,
      clearFuelTypeAttribution,
    });

  const [linehaulPrice, setLinehaulPrice] = useState<number>(0);

  // For Posthog logging
  const priceCalculatorProperties = useMemo(() => {
    return {
      serviceID,
      parentQuoteRequestId,
      mileage,
      finalPrice,
      fuelEstimate,
      profit: profit,
      profitType,
      carrierCost,
      carrierCostType,
      fuelSurcharge,
      selectedFuelType,
      stopFee,
      selectedQuoteType,
    };
  }, [
    serviceID,
    parentQuoteRequestId,
    mileage,
    finalPrice,
    fuelEstimate,
    profit,
    profitType,
    carrierCost,
    carrierCostType,
    fuelSurcharge,
    selectedFuelType,
    stopFee,
    selectedQuoteType,
  ]);

  useEffect(() => {
    setBuyInputValue(formatCostDisplay(carrierCost, carrierCostType));
  }, [carrierCost, carrierCostType, formatCostDisplay, selectedCurrency]);

  useEffect(() => {
    if (isEditingSellRef.current || isSellRateEditMode) {
      return;
    }

    setSellInputValue(
      isNaN(finalPrice ?? 0)
        ? ''
        : formatCostDisplay(finalPrice ?? 0, carrierCostType)
    );
  }, [
    finalPrice,
    carrierCostType,
    formatCostDisplay,
    selectedCurrency,
    isSellRateEditMode,
  ]);

  useEffect(() => {
    if (isEditingSellRef.current) {
      return;
    }

    // Syncs profitInputValue when the initialProfit (from QQF.profit),
    // or the relevant types (carrierCostType from CPC state, profitType from QQF prop) change.
    const displayProfit =
      profitType === ProfitType.Amount && isQuoteCurrencyEnabled
        ? toDisplayAmount(profit)
        : profit;
    setProfitInputValue(
      formatProfitByType(displayProfit, carrierCostType, profitType)
    );
  }, [
    profit,
    carrierCostType,
    profitType,
    isQuoteCurrencyEnabled,
    toDisplayAmount,
    selectedCurrency,
  ]);

  useEffect(() => {
    if (portalFuelSurcharge || customerTableFuelSurcharge != null) {
      setIsQuoteDetailsOpen(true);
    }
  }, [portalFuelSurcharge, customerTableFuelSurcharge]);

  useEffect(() => {
    const roundedInitMileage = _.round(initMileage);
    setMileage(roundedInitMileage);
  }, [initMileage]);

  const customAccessorialChargeKey = useMemo(
    () =>
      selectedCustomAccessorialDetails
        .map((acc) => `${acc.name}\0${acc.charge}`)
        .join('\n'),
    [selectedCustomAccessorialDetails]
  );

  useEffect(() => {
    if (selectedCustomAccessorialDetails.length === 0) {
      lastAppliedParseableAccessorialSumRef.current = 0;
      setAccessorialsDollarsInput('');
      return;
    }

    const sumUsd = sumFlatAccessorialCharges(selectedCustomAccessorialDetails);

    if (sumUsd === lastAppliedParseableAccessorialSumRef.current) {
      return;
    }
    lastAppliedParseableAccessorialSumRef.current = sumUsd;

    if (sumUsd <= 0) {
      setAccessorialsDollarsInput('');
      return;
    }

    const displaySum = isQuoteCurrencyEnabled
      ? toDisplayAmount(sumUsd)
      : sumUsd;
    setAccessorialsDollarsInput(String(_.round(displaySum, 2)));
    // Selection/charges only. Currency toggles are handled by the dedicated
    // accessorialsDollarsInput converter below — do not depend on currency
    // helpers here or both effects double-convert / wipe overrides.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- key captures selection
  }, [customAccessorialChargeKey]);

  useEffect(() => {
    if (prevFuelDisplayCurrencyRef.current === selectedCurrency) {
      return;
    }

    setFuelSurchargeInputValue(
      toDisplayAmount(fuelSurchargeRef.current).toFixed(2)
    );
    prevFuelDisplayCurrencyRef.current = selectedCurrency;
  }, [selectedCurrency, toDisplayAmount]);

  useEffect(() => {
    if (!isQuoteCurrencyEnabled) {
      prevQuoteCurrencyRef.current = selectedCurrency;
      return;
    }

    if (prevQuoteCurrencyRef.current === selectedCurrency) {
      return;
    }

    setAccessorialsDollarsInput((current) => {
      if (!current) {
        return current;
      }

      const parsed = parseFloat(current);
      if (!Number.isFinite(parsed) || parsed < 0) {
        return current;
      }

      const usdValue = convertDisplayToUsd(
        parsed,
        prevQuoteCurrencyRef.current
      );
      return String(convertUsdForDisplay(usdValue, selectedCurrency));
    });

    prevQuoteCurrencyRef.current = selectedCurrency;
  }, [isQuoteCurrencyEnabled, selectedCurrency]);

  useEffect(() => {
    const total = finalPrice ?? 0;
    if (carrierCostType === CarrierCostType.Flat) {
      setLinehaulPrice(total - accessorialsAddInQuoteUnits - fuelEstimate);
    } else {
      setLinehaulPrice(total - accessorialsAddInQuoteUnits - fuelSurcharge);
    }
  }, [
    finalPrice,
    fuelEstimate,
    fuelSurcharge,
    carrierCostType,
    accessorialsAddInQuoteUnits,
  ]);

  useEffect(() => {
    fetchEIAFuelPrice();
  }, []);

  useEffect(() => {
    setFuelEstimateHandler && setFuelEstimateHandler(mileage * fuelSurcharge);
  }, [mileage, fuelSurcharge, setFuelEstimateHandler]);

  const fetchEIAFuelPrice = async () => {
    const res = await getFuelPriceEIA();
    if (res.isOk()) {
      setFuelPriceEIA(res.value.fuelPrice);
    }
  };

  useEffect(() => {
    if (!setFinalPriceHandler) return;
    // Bill-rate edits set finalPrice directly; skip forward recalc while editing sell.
    if (isEditingSellRef.current || isSellRateEditModeRef.current) {
      return;
    }

    const numericProfitInput = Number(profitInputValue);
    const profitInUsd =
      profitType === ProfitType.Amount && isQuoteCurrencyEnabled
        ? fromDisplayAmount(numericProfitInput)
        : numericProfitInput;

    if (!isNaN(carrierCost) && !isNaN(numericProfitInput)) {
      let calculatedFinalPrice: number;

      if (profitType === ProfitType.Amount) {
        // Flat margin: Add the margin to the carrier cost
        calculatedFinalPrice = carrierCost + profitInUsd;
      } else {
        // ProfitType.Percentage
        if (isQuoteCalculatorMarginEnabled) {
          // Margin formula: Price = Cost / (1 - Profit_Percentage)
          if (numericProfitInput >= 100) {
            // Profit margin cannot be 100% or more.
            // Using 99.9% as a practical upper limit to avoid division by zero or negative.
            calculatedFinalPrice = carrierCost / (1 - 0.999); // e.g. carrierCost / 0.001 = carrierCost * 1000
          } else {
            calculatedFinalPrice = carrierCost / (1 - numericProfitInput / 100);
          }
        } else {
          // Markup formula: Price = Cost * (1 + Markup_Percentage)
          calculatedFinalPrice = carrierCost * (1 + numericProfitInput / 100);
        }
      }
      const nextTotal = _.round(
        calculatedFinalPrice + accessorialsAddInQuoteUnits,
        2
      );
      const currentTotal = _.round(finalPrice ?? 0, 2);
      if (Math.abs(nextTotal - currentTotal) < 0.01) {
        return;
      }
      setFinalPriceHandler(nextTotal);
    }
  }, [
    profitInputValue,
    carrierCost,
    profitType,
    carrierCostType,
    isQuoteCalculatorMarginEnabled,
    isQuoteCurrencyEnabled,
    fromDisplayAmount,
    setFinalPriceHandler,
    accessorialsAddInQuoteUnits,
    finalPrice,
  ]);

  const handleBuyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    setBuyInputValue(value);

    if (setSelectedQuoteIndexHandler) {
      setSelectedQuoteIndexHandler(null);
    }
    if (setCarrierCostHandler && value !== '') {
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        setCarrierCostHandler(fromDisplayAmount(numValue));
      }
    }
    if (setIsCarrierButtonClickedHandler) {
      setIsCarrierButtonClickedHandler(false);
    }
  };

  const handleProfitChange = (value: string) => {
    setProfitInputValue(value);

    // Compare the current value with the default price margin
    setIsDefaultProfit(Number(value) === profit);

    if (setProfitHandler && value !== '') {
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        const usdValue =
          profitType === ProfitType.Amount && isQuoteCurrencyEnabled
            ? fromDisplayAmount(numValue)
            : numValue;
        setProfitHandler(usdValue);
      }
    }
  };

  const handleSellChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    isEditingSellRef.current = true;
    setSellInputValue(value);

    if (value === '') {
      return;
    }

    const numValue = parseFloat(value);
    if (isNaN(numValue) || numValue < 0) {
      return;
    }

    const sellUsd = fromDisplayAmount(numValue);
    const quoteSell = sellUsd - accessorialsAddInQuoteUnits;
    const newProfit = calculateProfitFromFinalPrice({
      finalPrice: quoteSell,
      carrierCost,
      profitType,
      isMarginEnabled: isQuoteCalculatorMarginEnabled,
    });

    const displayProfit =
      profitType === ProfitType.Amount && isQuoteCurrencyEnabled
        ? toDisplayAmount(newProfit)
        : newProfit;
    setProfitInputValue(
      formatProfitByType(displayProfit, carrierCostType, profitType)
    );
    setIsDefaultProfit(false);
    setProfitHandler?.(newProfit);
    setFinalPriceHandler?.(sellUsd);
  };

  const exitSellRateEditMode = useCallback(() => {
    isEditingSellRef.current = false;
    isSellRateEditModeRef.current = false;
    setIsSellRateEditMode(false);
    syncBillRateEditSessionActive(false);
    setSellInputValue(
      isNaN(finalPrice ?? 0)
        ? ''
        : formatCostDisplay(finalPrice ?? 0, carrierCostType)
    );
  }, [
    carrierCostType,
    finalPrice,
    formatCostDisplay,
    syncBillRateEditSessionActive,
  ]);

  const enterSellRateEditMode = useCallback(() => {
    if (isSellRateEditMode) {
      return;
    }

    isEditingSellRef.current = true;
    isSellRateEditModeRef.current = true;
    setIsSellRateEditMode(true);
    syncBillRateEditSessionActive(true);
    requestAnimationFrame(() => {
      sellInputRef.current?.focus();
      sellInputRef.current?.select();
    });
  }, [isSellRateEditMode, syncBillRateEditSessionActive]);

  const handleSellFocus: FocusEventHandler<HTMLInputElement> = () => {
    if (isTql && !isSellRateEditMode) {
      enterSellRateEditMode();
      return;
    }

    isEditingSellRef.current = true;
    syncBillRateEditSessionActive(true);
  };

  const handleSellBlur: FocusEventHandler<HTMLInputElement> = (e) => {
    if (isTql) {
      if (isSellRateEditMode) {
        exitSellRateEditMode();
      }
    } else {
      isEditingSellRef.current = false;
      syncBillRateEditSessionActive(false);
      setSellInputValue(
        isNaN(finalPrice ?? 0)
          ? ''
          : formatCostDisplay(finalPrice ?? 0, carrierCostType)
      );
    }
    onBlurHandler?.(e);
  };

  const handleChangeMileage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setMileage(parseFloat(value));
  };

  const handleChangeFuelSurcharge = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;

    if (/^(\d+(\.\d{0,2})?|\.\d{0,2})?$/.test(value)) {
      markManualFuelEntry();
      setFuelSurchargeInputValue(value);

      const numericValue = parseFloat(value);
      if (!isNaN(numericValue)) {
        setFuelSurcharge(fromDisplayAmount(numericValue));
      } else {
        setFuelSurcharge(0);
      }
    }
  };

  const handleProfitTypeToggle = () => {
    const newProfitType =
      profitType === ProfitType.Amount
        ? ProfitType.Percentage
        : ProfitType.Amount;

    const currentNumericProfit = Number(profitInputValue);
    const profitInUsd =
      profitType === ProfitType.Amount && isQuoteCurrencyEnabled
        ? fromDisplayAmount(currentNumericProfit)
        : currentNumericProfit;
    const currentCarrierCost = carrierCost;
    let currentFinalPrice: number;

    // Determine the current finalPrice based on existing values and calculation mode
    if (profitType === ProfitType.Amount) {
      currentFinalPrice = currentCarrierCost + profitInUsd;
    } else {
      // ProfitType.Percentage
      if (isQuoteCalculatorMarginEnabled) {
        if (currentNumericProfit >= 100) {
          currentFinalPrice = currentCarrierCost / (1 - 0.999);
        } else {
          currentFinalPrice =
            currentCarrierCost / (1 - currentNumericProfit / 100);
        }
      } else {
        currentFinalPrice =
          currentCarrierCost * (1 + currentNumericProfit / 100);
      }
    }

    let newProfitValue: number;

    if (newProfitType === ProfitType.Percentage) {
      // Switching from Flat ($) to Percentage (%)
      // New Profit % = ((Final Price / Carrier Cost) - 1) * 100 for markup
      // New Profit % = (1 - (Carrier Cost / Final Price)) * 100 for margin
      if (currentCarrierCost > 0 && currentFinalPrice > 0) {
        if (isQuoteCalculatorMarginEnabled) {
          if (currentFinalPrice === currentCarrierCost) {
            // Avoid division by zero or negative if cost equals price (0% margin)
            newProfitValue = 0;
          } else if (currentFinalPrice < currentCarrierCost) {
            // Loss scenario, represent as negative margin
            newProfitValue = (1 - currentCarrierCost / currentFinalPrice) * 100; // will be negative
          } else {
            newProfitValue = (1 - currentCarrierCost / currentFinalPrice) * 100;
          }
        } else {
          newProfitValue = (currentFinalPrice / currentCarrierCost - 1) * 100;
        }
      } else {
        newProfitValue = 0; // Default to 0 if carrierCost or finalPrice is zero
      }
    } else {
      // Switching from Percentage (%) to Flat ($)
      // New Profit $ = Final Price - Carrier Cost
      newProfitValue = currentFinalPrice - currentCarrierCost;
    }

    const displayNewProfit =
      newProfitType === ProfitType.Amount && isQuoteCurrencyEnabled
        ? toDisplayAmount(newProfitValue)
        : newProfitValue;
    const newProfitFormatted = formatProfitByType(
      displayNewProfit,
      carrierCostType,
      newProfitType
    );

    setProfitInputValue(newProfitFormatted);
    setProfitHandler?.(newProfitValue);

    // Update whether new margin value matches default
    if (profitType && profitType === newProfitType) {
      setIsDefaultProfit(newProfitValue === profit);
    }
    if (profitType) {
      setIsDefaultProfitType(newProfitType === profitType);
    }

    // Toggle the margin type
    setProfitTypeHandler(newProfitType);
  };

  const handleSelectedFuelToggle = (newType: FuelType) => {
    selectUserFuelType(newType);
  };

  const handleFuelSurchargeBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    onBlurHandler?.(e);

    const numericValue = parseFloat(fuelSurchargeInputValue);
    if (!isNaN(numericValue)) {
      const usdValue = fromDisplayAmount(numericValue);
      setFuelSurchargeInputValue(toDisplayAmount(usdValue).toFixed(2));
      setFuelSurcharge(usdValue);
    } else {
      setFuelSurchargeInputValue('0.30');
      setFuelSurcharge(0.3);
    }
  };

  const handleCarrierCostTypeToggle = () => {
    if (buyInputValue) {
      const currentCarrierCost = carrierCost;
      const currentProfit = profit;
      const newCarrierCostType =
        carrierCostType === CarrierCostType.Flat
          ? CarrierCostType.PerMile
          : CarrierCostType.Flat;

      let newCarrierCost: number;
      let newProfit = currentProfit;

      if (newCarrierCostType === CarrierCostType.PerMile) {
        // Convert from Flat to Per Mile
        newCarrierCost = currentCarrierCost / mileage;
        // Convert margin value when switching types
        if (profitType === ProfitType.Amount) {
          newProfit = currentProfit / mileage;
        }
      } else {
        // Convert from Per Mile to Flat
        newCarrierCost = currentCarrierCost * mileage;
        // Convert margin value when switching types
        if (profitType === ProfitType.Amount) {
          newProfit = currentProfit * mileage;
        }
      }

      setCarrierCostHandler?.(newCarrierCost);
      setProfitHandler?.(newProfit);
      setBuyInputValue(formatCostDisplay(newCarrierCost, newCarrierCostType));
      setProfitInputValue(
        formatProfitByType(
          profitType === ProfitType.Amount && isQuoteCurrencyEnabled
            ? toDisplayAmount(newProfit)
            : newProfit,
          newCarrierCostType,
          profitType
        )
      );
      setCarrierCostTypeHandler(newCarrierCostType);
    }
  };

  const handleCopySellPriceToClipboard = async () => {
    if (!finalPrice) {
      toast({
        description: 'No quote to submit.',
        variant: 'info',
      });
      return;
    }

    const roundedFinalPrice = formatCostDisplay(finalPrice, carrierCostType);

    try {
      const success = await copyToClipboard(roundedFinalPrice);
      if (success) {
        setHasCopiedSellPrice(true);
        setTimeout(() => setHasCopiedSellPrice(false), 2000);
      }

      // Non-blocking call to terminatingActionHandler
      try {
        terminatingActionHandler(
          QuoteTerminatingAction.Copy,
          QuoteValueType.TotalSell
        );
      } catch (err) {
        captureException(err, {
          functionName:
            'CarrierPriceCalculator handleCopySellPriceToClipboard.terminatingActionHandler',
        });
      }
    } catch (error) {
      captureException(error, {
        functionName: 'CarrierPriceCalculator handleCopySellPriceToClipboard',
      });

      toast({
        description: 'Failed to copy to clipboard.',
        variant: 'destructive',
      });
    }
  };

  const handleCopyLinehaulToClipboard = async () => {
    const roundedLinehaulPrice = formatCostDisplay(
      linehaulPrice,
      carrierCostType
    );

    try {
      const success = await copyToClipboard(roundedLinehaulPrice);
      if (success) {
        setHasCopiedLinehaulPrice(true);
        setTimeout(() => setHasCopiedLinehaulPrice(false), 2000);
      }

      // Non-blocking call to terminatingActionHandler
      try {
        terminatingActionHandler(
          QuoteTerminatingAction.Copy,
          QuoteValueType.Linehaul
        );
      } catch (err) {
        captureException(err, {
          functionName:
            'CarrierPriceCalculator handleCopyLinehaulToClipboard.terminatingActionHandler',
        });
      }
    } catch (error) {
      captureException(error, {
        functionName: 'CarrierPriceCalculator handleCopyLinehaulToClipboard',
      });

      toast({
        description: 'Failed to copy to clipboard.',
        variant: 'destructive',
      });
    }
  };

  // Kenco 0 is treated as unavailable elsewhere (truthy checks), omit it here so a
  // zero rate does not inflate toggle visibility. Customer table 0 is a real rate.
  let validFuelValues = [
    datFuelSurcharge,
    portalFuelSurcharge,
    fuelPriceEIA,
    customerTableFuelSurcharge,
  ].filter((v) => v != null);

  // Kenco bases rates on their own FSC Table. When valid, it's the only rate we need to show.
  if (kencoFuelSurcharge) {
    validFuelValues = [kencoFuelSurcharge];
  }

  return (
    <>
      {showTitle && (
        <Typography
          variant='h6'
          weight='medium'
          className='mb-3 text-neutral-500'
        >
          {isTql ? 'Calculate Bill Rate' : 'Calculate Final Price'}
        </Typography>
      )}

      <Flex direction='col' gap='sm' className='mb-4'>
        <Flex gap='md' align='baseline' className='mb-2'>
          {/* Carrier Cost */}
          <Flex direction='col' gap='xs' className='min-w-0 flex-1'>
            <Label name='buy' className='text-neutral-800 text-sm'>
              {buyLabel}
            </Label>
            <Flex direction='col' align='center' gap='xs' className='relative'>
              <Flex align='center' className='relative'>
                <span className={currencyPrefixClass}>
                  {displayInputPrefix}
                </span>
                <Input
                  type='number'
                  value={buyInputValue}
                  onChange={handleBuyChange}
                  onBlur={onBlurHandler ?? undefined}
                  className={cn(
                    currencyInputPaddingClass,
                    'pr-1 py-1.5 text-sm'
                  )}
                  min={0}
                  step={carrierCostType === CarrierCostType.PerMile ? 0.01 : 1}
                  aria-label='Carrier cost input'
                />
              </Flex>

              {Boolean(mileage) && (
                <Flex
                  gap='xs'
                  className='text-[11px] absolute bottom-1.75 right-2'
                >
                  <button
                    type='button'
                    title={'Flat Rate'}
                    onClick={handleCarrierCostTypeToggle}
                    className={`transition-colors  ${
                      carrierCostType === CarrierCostType.Flat
                        ? 'text-brand-400 font-bold underline'
                        : 'text-neutral-500 hover:text-brand-400'
                    }`}
                  >
                    Flat
                  </button>
                  <button
                    title={'Per Mile'}
                    type='button'
                    onClick={handleCarrierCostTypeToggle}
                    className={`transition-colors  ${
                      carrierCostType === CarrierCostType.PerMile
                        ? 'text-brand-400 font-bold underline'
                        : 'text-neutral-500 hover:text-brand-400'
                    }`}
                  >
                    /mi
                  </button>
                </Flex>
              )}
            </Flex>
          </Flex>

          {/* Markup or Margin */}
          <Flex direction='col' gap='xs' className='min-w-0 flex-1'>
            <Label name='margin' className='text-neutral-800 text-sm'>
              {isQuoteCalculatorMarginEnabled ? 'Margin' : 'Markup'}
            </Label>
            <Flex direction='col' align='center' gap='xs' className='relative'>
              <Flex align='center' className='relative'>
                {profitType === ProfitType.Amount && (
                  <span className={currencyPrefixClass}>
                    {displayInputPrefix}
                  </span>
                )}
                <Input
                  type='number'
                  value={profitInputValue}
                  onChange={(e) => handleProfitChange(e.target.value)}
                  onBlur={onBlurHandler ?? undefined}
                  className={cn(
                    `min-w-[60px] text-sm [appearance:textfield]`,
                    profitType === ProfitType.Percentage && 'pl-2',
                    profitType === ProfitType.Amount &&
                      currencyInputPaddingClass
                  )}
                  min={0}
                  step={
                    profitType === ProfitType.Percentage ||
                    carrierCostType === CarrierCostType.PerMile
                      ? 0.01
                      : 1
                  }
                  aria-label='Margin input'
                />

                <KitDefaultProfitTooltip
                  profitInputValue={profitInputValue}
                  profitInputType={profitType}
                  isDefaultProfit={isDefaultProfit}
                  isDefaultProfitType={isDefaultProfitType}
                  setIsDefaultProfit={setIsDefaultProfit}
                  setIsDefaultProfitType={setIsDefaultProfitType}
                />
              </Flex>

              {/* Markup or Margin Type Toggle */}
              <Flex
                gap='xs'
                className='text-[11px] absolute bottom-1.75 right-2'
              >
                <button
                  type='button'
                  title={'Use Dollar Profit'}
                  onClick={handleProfitTypeToggle}
                  className={`transition-colors  ${
                    profitType === ProfitType.Amount
                      ? 'text-brand-400 font-bold underline'
                      : 'text-neutral-500 hover:text-brand-400'
                  }`}
                >
                  Flat
                </button>
                <button
                  type='button'
                  title={'Use Percentage Profit'}
                  onClick={handleProfitTypeToggle}
                  className={`transition-colors  ${
                    profitType === ProfitType.Percentage
                      ? 'text-brand-400 font-bold underline'
                      : 'text-neutral-500 hover:text-brand-400'
                  }`}
                >
                  %
                </button>
              </Flex>
            </Flex>
          </Flex>
        </Flex>

        {selectedCustomAccessorialDetails.length > 0 && (
          <>
            <Flex gap='xs' align='center' className='w-full'>
              <Divider className='border border-neutral-400' />
              <Typography
                variant='body'
                weight='semibold'
                className='text-neutral-400 mb-1'
              >
                +
              </Typography>
              <Divider className='border border-neutral-400' />
            </Flex>

            <Flex direction='col' gap='md' className='w-full'>
              {/* Accessorials Info */}
              <Flex
                direction='col'
                gap='xs'
                className='w-full rounded border border-info bg-info-50 p-3'
              >
                <Flex direction='col'>
                  <Typography
                    variant='body-sm'
                    weight='semibold'
                    className='text-info-800'
                  >
                    Selected Accessorials
                  </Typography>
                  <Typography variant='body-xxs' className='text-info-600'>
                    Hover over each accessorial for additional details
                  </Typography>
                </Flex>

                <Divider className='border border-info-200' />

                <Flex direction='col' gap='sm' className='w-full'>
                  {selectedCustomAccessorialDetails.map((acc, index) => (
                    <QuickQuoteCustomAccessorialTooltipRow
                      key={`${acc.name}-${index}`}
                      accessorial={acc}
                    />
                  ))}
                </Flex>
              </Flex>

              {/* Accessorials Input */}
              <Flex direction='col' gap='xs' className='w-full'>
                <Typography
                  variant='body-xxs'
                  weight='medium'
                  className='text-accent-500'
                >
                  {`After calculating a total accessorial rate with the charges above, `}
                  {`enter the total rate below to update your final ${isTql ? 'bill' : 'sell'} rate.`}
                </Typography>

                <Label name='accessorials' className='text-neutral-800 text-sm'>
                  Accessorials
                </Label>
                <Flex align='center' className='w-full relative'>
                  <span className={currencyPrefixClass}>
                    {displayInputPrefix}
                  </span>
                  <Input
                    type='text'
                    inputMode='decimal'
                    value={accessorialsDollarsInput}
                    onChange={(e) =>
                      setAccessorialsDollarsInput(e.target.value)
                    }
                    onBlur={(e) => {
                      onBlurHandler?.(e);
                      const trimmed = accessorialsDollarsInput.trim();
                      if (trimmed === '') {
                        return;
                      }
                      const n = parseFloat(trimmed);
                      if (Number.isFinite(n) && n >= 0) {
                        setAccessorialsDollarsInput(String(n));
                      } else {
                        setAccessorialsDollarsInput('');
                      }
                    }}
                    className={cn(
                      currencyInputPaddingClass,
                      'pr-1 py-1.5 text-sm'
                    )}
                    aria-label='Accessorials amount in dollars'
                  />
                </Flex>
              </Flex>
            </Flex>

            <Flex gap='xs' align='center' className='w-full'>
              <Divider className='border border-neutral-400' />
              <Typography
                variant='body'
                weight='semibold'
                className='text-neutral-400 mb-1'
              >
                =
              </Typography>
              <Divider className='border border-neutral-400' />
            </Flex>
          </>
        )}

        {/* Final Price */}
        <Flex direction='col' gap='xs' className='relative w-full'>
          <Label name='sell' className='text-neutral-800 text-sm'>
            {sellLabel}
          </Label>
          <Flex align='center' className='relative w-full min-h-10'>
            <span
              className={
                isSellTypingActive
                  ? currencyPrefixClass
                  : sellCurrencyPrefixClass
              }
            >
              {displayInputPrefix}
            </span>
            <Input
              ref={sellInputRef}
              className={cn(
                'h-10! transition-colors',
                isSellTypingActive
                  ? cn(currencyInputPaddingClass, 'pr-24 py-2 text-sm')
                  : cn(
                      'pr-24 py-2 text-lg! font-bold! bg-brand-50! text-brand-400! border-brand!',
                      sellInputPaddingClass,
                      isTql && 'hover:bg-brand-100! hover:border-brand-500!'
                    )
              )}
              style={
                isTql && !isSellRateEditMode
                  ? { cursor: tqlBillRateEditCursor }
                  : undefined
              }
              title={
                isTql && !isSellRateEditMode
                  ? 'Click to edit bill rate'
                  : undefined
              }
              data-testid='sell-rate-input'
              type={isSellTypingActive ? 'number' : 'text'}
              value={sellInputValue}
              min={0}
              step={carrierCostType === CarrierCostType.PerMile ? 0.01 : 1}
              onChange={isSellTypingActive ? handleSellChange : undefined}
              onFocus={handleSellFocus}
              onBlur={handleSellBlur}
              readOnly={isTql && !isSellRateEditMode}
              aria-label='Final price'
            />

            {Boolean(mileage) && finalPrice && (
              <Typography
                variant='body-xs'
                weight='bold'
                className={cn(
                  'pointer-events-none absolute right-2 top-1/2 -translate-y-1/2',
                  !isSellTypingActive ? 'text-brand-400' : 'text-neutral-500'
                )}
              >
                {carrierCostType === CarrierCostType.PerMile && (
                  <>
                    {`(${formatMoneyLabel(
                      finalPrice * mileage,
                      CarrierCostType.Flat
                    )} Flat)`}
                  </>
                )}

                {carrierCostType === CarrierCostType.Flat && (
                  <>
                    {`(${formatMoneyLabel(
                      finalPrice / mileage,
                      CarrierCostType.PerMile
                    )}/mi)`}
                  </>
                )}
              </Typography>
            )}

            <Button
              buttonNamePosthog={
                calculatorParent === CarrierPriceCalculatorParent.QuickQuote
                  ? ButtonNamePosthog.QuickQuoteCopySellPrice
                  : ButtonNamePosthog.CarrierQuoteCopySellPrice
              }
              logProperties={priceCalculatorProperties}
              className={cn(
                'absolute h-4 w-4 p-0 -top-5 right-1 border-none',
                hasCopiedSellPrice ? 'cursor-default' : 'cursor-pointer'
              )}
              variant='ghost'
              type='button'
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                !hasCopiedSellPrice && handleCopySellPriceToClipboard();
              }}
            >
              {hasCopiedSellPrice ? (
                <Tooltip open={true}>
                  <TooltipTrigger asChild>
                    <CheckIcon className='h-4 w-4' />
                  </TooltipTrigger>
                  <TooltipContent>Copied!</TooltipContent>
                </Tooltip>
              ) : (
                <CopyIcon className='h-4 w-4' />
              )}
            </Button>
          </Flex>

          {!finalPrice && (
            <Typography variant='body-xs' className='text-error-500'>
              Required
            </Typography>
          )}
        </Flex>

        {setFuelEstimateHandler && isQuickQuoteFuelDetailsEnabled && (
          <Button
            className='w-40 h-8 mx-auto mt-2 hover:border-neutral-600 hover:bg-neutral-200'
            buttonNamePosthog={ButtonNamePosthog.ToggleQuoteDetails}
            onClick={() => setIsQuoteDetailsOpen(!isQuoteDetailsOpen)}
            type='button'
            variant='ghost'
            data-testid='quick-quote-toggle-fuel-details'
          >
            <Flex align='center' gap='xs'>
              {!isQuoteDetailsOpen ? (
                <ChevronDownIcon className='h-4 w-4 mr-1' />
              ) : (
                <ChevronUpIcon className='h-4 w-4 mr-1' />
              )}
              <Typography
                as='span'
                weight='medium'
                className='text-neutral-600 text-[13px]'
              >
                {!isQuoteDetailsOpen
                  ? 'Show fuel details'
                  : 'Hide fuel details'}
              </Typography>
            </Flex>
          </Button>
        )}

        {isQuoteDetailsOpen && setFuelEstimateHandler && (
          <Flex direction='col' gap='md' className='mt-1'>
            <Flex gap='sm' className='min-w-0 flex-1'>
              {/* Mileage for Fuel */}
              <Flex direction='col' gap='xs' className='min-w-0 w-[110px]'>
                <Label name='mileage' className='text-neutral-800 text-sm!'>
                  Distance
                </Label>
                <Flex align='center' className='relative'>
                  <Input
                    type='number'
                    value={mileage}
                    onChange={handleChangeMileage}
                    onBlur={onBlurHandler ?? undefined}
                    className='pl-2 pr-2 py-1.5 text-sm'
                    min={0}
                    aria-label='Mileage input'
                  />
                  <div className='absolute bottom-1.5 right-2 text-[11px] text-neutral-400'>
                    mi
                  </div>
                </Flex>
              </Flex>

              {/* Fuel Surcharge */}
              {/* TODO: Add AI icon */}
              <Flex direction='col' align='start' gap='xs' className='min-w-0'>
                <Flex justify='between' align='center' className='w-full'>
                  <Label name='fsc' className='text-neutral-800 text-sm!'>
                    FSC
                  </Label>
                  <Tooltip delayDuration={10}>
                    <TooltipTrigger asChild>
                      <InfoIcon className='h-4 w-4 mr-1' />
                    </TooltipTrigger>
                    <TooltipContent>
                      {selectedFuelType === FuelType.DOE && (
                        <div>
                          <Typography variant='body-sm'>
                            Rate assumes 7 miles-per-gallon
                          </Typography>
                          <Typography className='text-[12px] italic mt-2'>
                            {`This week's DOE Diesel Price: $${fuelPriceEIA}/gallon`}
                          </Typography>
                        </div>
                      )}
                      {selectedFuelType === FuelType.Portal &&
                        portalFuelSurchargeSource && (
                          <Typography variant='body-sm'>
                            {`AI detected FSC from ${integrationNameMap[portalFuelSurchargeSource]}`}
                          </Typography>
                        )}
                      {selectedFuelType === FuelType.DAT && (
                        <Typography>{`DAT estimated FSC`}</Typography>
                      )}
                      {selectedFuelType === FuelType.Kenco && (
                        <Typography variant='body-sm'>
                          {`$${_.round(kencoFuelSurcharge ?? 0, 2)}/mi FSC from ${integrationNameMap[Quoting.KencoFlash]}'s DAT data`}
                        </Typography>
                      )}
                      {selectedFuelType === FuelType.CustomerTable && (
                        <Typography variant='body-sm'>
                          Customer FSC table rate for this quote
                        </Typography>
                      )}
                    </TooltipContent>
                  </Tooltip>
                </Flex>

                <Flex
                  direction='col'
                  align='start'
                  gap='xs'
                  className='min-w-0'
                >
                  <Flex align='center' className='relative'>
                    <div className={currencyPrefixClass}>
                      {displayInputPrefix}
                    </div>
                    <Input
                      type='number'
                      value={fuelSurchargeInputValue}
                      onChange={handleChangeFuelSurcharge}
                      onBlur={handleFuelSurchargeBlur}
                      className={cn(
                        'min-w-[60px] text-sm',
                        isCadDisplay ? 'pl-10' : 'pl-4 xxs:pl-5'
                      )}
                      min={0}
                      step={0.01}
                      aria-label='Fuel Surcharge input'
                    />
                  </Flex>

                  {validFuelValues.length >= 2 && (
                    <Flex
                      wrap='wrap'
                      gap='xs'
                      justify='start'
                      className='text-[11px] leading-none'
                    >
                      {customerTableFuelSurcharge != null && (
                        <button
                          type='button'
                          title='Use customer FSC table rate'
                          onClick={() =>
                            handleSelectedFuelToggle(FuelType.CustomerTable)
                          }
                          className={cn(
                            'transition-colors',
                            selectedFuelType === FuelType.CustomerTable
                              ? 'text-brand-400 font-bold underline'
                              : 'text-neutral-400 hover:text-brand-main'
                          )}
                        >
                          Table
                        </button>
                      )}
                      {fuelPriceEIA && !kencoFuelSurcharge && (
                        <button
                          type='button'
                          title={'Use DOE Fuel Rate'}
                          onClick={() => handleSelectedFuelToggle(FuelType.DOE)}
                          className={`transition-colors  ${
                            selectedFuelType === FuelType.DOE
                              ? 'text-brand-400 font-bold underline'
                              : 'text-neutral-400 hover:text-brand-main'
                          }`}
                        >
                          DOE
                        </button>
                      )}
                      {kencoFuelSurcharge && (
                        <button
                          type='button'
                          title='Use Kenco fuel rate'
                          onClick={() =>
                            handleSelectedFuelToggle(FuelType.Kenco)
                          }
                          className={cn(
                            'transition-colors',
                            selectedFuelType === FuelType.Kenco
                              ? 'text-brand-400 font-bold underline'
                              : 'text-neutral-400 hover:text-brand-main'
                          )}
                        >
                          Kenco
                        </button>
                      )}
                      {datFuelSurcharge && (
                        <button
                          type='button'
                          title={'Use DAT Fuel Rate'}
                          onClick={() => handleSelectedFuelToggle(FuelType.DAT)}
                          className={cn(
                            'transition-colors',
                            selectedFuelType === FuelType.DAT
                              ? 'text-brand-400 font-bold underline'
                              : 'text-neutral-400 hover:text-brand-main'
                          )}
                        >
                          DAT
                        </button>
                      )}
                      {portalFuelSurcharge && (
                        <button
                          type='button'
                          title={`Use ${
                            portalFuelSurchargeSource
                              ? integrationNameMap[portalFuelSurchargeSource]
                              : 'Portal'
                          } Fuel Rate`}
                          onClick={() =>
                            handleSelectedFuelToggle(FuelType.Portal)
                          }
                          className={cn(
                            'transition-colors',
                            selectedFuelType === FuelType.Portal
                              ? 'text-brand-400 font-bold underline'
                              : 'text-neutral-400 hover:text-brand-main'
                          )}
                        >
                          {portalFuelSurchargeSource
                            ? integrationNameMap[portalFuelSurchargeSource]
                            : 'Portal'}
                        </button>
                      )}
                    </Flex>
                  )}
                </Flex>
              </Flex>

              {/* Final Fuel Price */}
              <Flex direction='col' gap='xs'>
                <Label name='totalFuel' className='text-neutral-800 text-sm!'>
                  Fuel
                </Label>
                <Flex align='center' className='relative'>
                  <span className={currencyPrefixClass}>
                    {displayInputPrefix}
                  </span>
                  <Input
                    className={cn(
                      currencyInputPaddingClass,
                      'pr-2 py-1.5 transition-all text-sm w-[70px] read-only:bg-neutral-50 read-only:text-neutral-400 read-only:border-neutral-400'
                    )}
                    type='text'
                    value={
                      isNaN(mileage * fuelSurcharge)
                        ? ''
                        : formatCostDisplay(
                            mileage * fuelSurcharge,
                            CarrierCostType.Flat
                          )
                    }
                    disabled
                    aria-label='Final Fuel Price'
                  />
                </Flex>
              </Flex>
            </Flex>

            <Flex
              direction='col'
              align='start'
              gap='xs'
              className='w-full relative'
            >
              <Label name='linehaul' className='text-neutral-800 text-sm!'>
                <Flex gap='xs' align='baseline' className='w-full'>
                  Linehaul
                  <Typography
                    variant='body-xxs'
                    className='text-neutral-400 italic font-medium'
                  >
                    {selectedCustomAccessorialDetails?.length > 0
                      ? ` (${sellRatePhrase} - Fuel - Accessorials)`
                      : ` (${sellRatePhrase} - Fuel)`}
                  </Typography>
                </Flex>
              </Label>

              <Flex align='center' className='relative w-full'>
                <span className={currencyPrefixClass}>
                  {displayInputPrefix}
                </span>
                <Input
                  className={cn(
                    currencyInputPaddingClass,
                    'pr-2 py-1.5 text-sm read-only:bg-neutral-50 min-w-[90px] read-only:text-neutral-400 read-only:border-neutral-400'
                  )}
                  type='text'
                  value={
                    isNaN(linehaulPrice)
                      ? ''
                      : formatCostDisplay(linehaulPrice, carrierCostType)
                  }
                  disabled
                  aria-label='Linehaul'
                />
                {carrierCostType === CarrierCostType.PerMile && (
                  <div className='absolute bottom-1.5 right-1 xxs:right-2 text-xs text-neutral-400'>
                    /mi
                  </div>
                )}
              </Flex>

              {/* Show total linehaul or per-mile value below, styled like Sell price */}
              {Boolean(mileage) && linehaulPrice && (
                <Flex
                  justify='center'
                  className='absolute bottom-1.5 right-2 text-[11px]'
                >
                  <Typography
                    variant='body-xs'
                    className='text-neutral-400 font-medium'
                  >
                    {carrierCostType === CarrierCostType.PerMile && (
                      <>
                        {`(${formatMoneyLabel(
                          linehaulPrice * mileage,
                          CarrierCostType.Flat
                        )} Flat)`}
                      </>
                    )}

                    {carrierCostType === CarrierCostType.Flat && (
                      <>
                        {`(${formatMoneyLabel(
                          linehaulPrice / mileage,
                          CarrierCostType.PerMile
                        )}/mi)`}
                      </>
                    )}
                  </Typography>
                </Flex>
              )}

              <Button
                buttonNamePosthog={
                  calculatorParent === CarrierPriceCalculatorParent.QuickQuote
                    ? ButtonNamePosthog.QuickQuoteCopyLinehaulPrice
                    : ButtonNamePosthog.CarrierQuoteCopyLinehaulPrice
                }
                logProperties={priceCalculatorProperties}
                className={cn(
                  'absolute h-4 p-0 top-1 right-1 border-none',
                  hasCopiedLinehaulPrice ? 'cursor-default' : 'cursor-pointer'
                )}
                variant='ghost'
                type='button'
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  !hasCopiedLinehaulPrice && handleCopyLinehaulToClipboard();
                }}
              >
                {hasCopiedLinehaulPrice ? (
                  <Tooltip open={true}>
                    <TooltipTrigger asChild>
                      <CheckIcon className='h-4 w-4' />
                    </TooltipTrigger>
                    <TooltipContent>Copied!</TooltipContent>
                  </Tooltip>
                ) : (
                  <CopyIcon className='h-4 w-4' />
                )}
              </Button>
            </Flex>
          </Flex>
        )}
      </Flex>
    </>
  );
}

export const formatCostByType = (value: number, type: CarrierCostType) => {
  if (type === CarrierCostType.PerMile) {
    // Convert to string with max 2 decimals
    const formatted = value.toFixed(2);

    // Remove trailing zeros and decimal point if not needed
    return formatted.replace(/\.?0{2}$/, '');
  }
  // If flat rate, support only whole numbers
  return value.toFixed(0);
};

const formatProfitByType = (
  value: number,
  costType: CarrierCostType,
  profitType: ProfitType
) => {
  if (profitType === ProfitType.Percentage) {
    const formatted = value.toFixed(2);
    // Up to 2 decimal places; strip a trailing ".00" (same as flat /mi margin)
    return formatted.replace(/\.?0{2}$/, '');
  }

  // If cost type is per mile and margin type is flat, format margin to 2 decimal places
  if (costType === CarrierCostType.PerMile) {
    // Convert to string with max 2 decimals
    const formatted = value.toFixed(2);

    // Remove trailing zeros and decimal point if not needed
    return formatted.replace(/\.?0{2}$/, '');
  } else {
    return value.toFixed(0);
  }
};
